import { memo, useMemo } from "react"
import { getDate } from "./module"
import { useInspectorPanelUsage } from "./useInspectorPanelUsage"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { useImagesDates } from "./useImageDates"
import {
    GenericUpload,
    InfraredProcessingMode,
    OrthoProcessingMode,
    SidePanelLayer,
} from "@nova/constants"
import { config } from "@nova/configuration/browser"
import { DateFormat, TimeFormat, formatBytes } from "@nova/format"
import classNames from "classnames"
import { ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { UserProfileModalTrigger } from "features/users/UserProfileModal"

const InspectorPanelMetadata = memo(
    ({
        entity,
        uploads,
        uploadSize,
        completedUploadCount,
        hideCreator,
        hideFileCount,
        className = "mx-4",
        isSingleFile,
        onShowImagesClick,
    }: {
        entity: SidePanelLayer
        uploads?: GenericUpload[]
        uploadSize?: number
        completedUploadCount: number
        hideFileCount?: boolean
        hideCreator?: boolean
        className?: string
        isSingleFile?: boolean
        onShowImagesClick?: () => void
    }) => {
        const { organizationSettingDateFormat } = config.use("organizationSettingDateFormat")
        const { organizationSettingTimeFormat } = config.use("organizationSettingTimeFormat")
        const dateCreated = useMemo(
            () =>
                getDate(
                    entity?.createdAt,
                    organizationSettingDateFormat as DateFormat,
                    organizationSettingTimeFormat as TimeFormat
                ),
            [entity?.createdAt, organizationSettingDateFormat, organizationSettingTimeFormat]
        )
        const processingMode = useMemo(() => {
            if (!!entity && "job" in entity && entity.job && "processingMode" in entity.job) {
                const mode = entity.job.processingMode
                if (mode === OrthoProcessingMode.Urban) {
                    return "Urban"
                } else if (
                    mode === OrthoProcessingMode.Rapid ||
                    mode === InfraredProcessingMode.Rapid
                ) {
                    return "Rapid"
                }
                return "General"
            }
            return undefined
        }, [entity])
        const usage = useInspectorPanelUsage(entity, completedUploadCount)
        const { fullName } = useFullnameForUserId(entity?.createdBy?.userId)

        const { startDate, endDate } = useImagesDates(uploads as GenericUpload[])
        const fileCount = uploads?.length
        return (
            <div>
                {!hideCreator && (
                    <div className="py-2 border-t border-t-gray-700 mx-4 text-gray-50">
                        {/* Creator */}
                        <small className="select-none">Creator:</small>
                        <div>
                            <UserProfileModalTrigger userId={entity?.createdBy?.userId}>
                                <div className="flex gap-2 items-center mt-1">
                                    <div className="w-6 min-w-[24px] aspect-square rounded overflow-hidden">
                                        <ProfilePhoto
                                            userId={entity?.createdBy?.userId || ""}
                                            fullName={fullName || ""}
                                        />
                                    </div>
                                    <small className="text-gray-0 group-hover/UserProfileModalTrigger:underline">
                                        {fullName || "Unknown"}
                                    </small>
                                </div>
                            </UserProfileModalTrigger>
                        </div>
                    </div>
                )}
                {onShowImagesClick && (
                    <div className="w-full py-2 px-4">
                        <ButtonTertiary
                            LeftIcon={Icons.PhotoLibrary}
                            className="w-full"
                            title="View Images"
                            onClick={onShowImagesClick}
                        />
                    </div>
                )}
                <div
                    className={classNames(
                        "py-2 border-t border-t-gray-700 flex flex-col gap-2 text-gray-50",
                        className
                    )}
                >
                    {/* Files Count */}
                    {!hideFileCount && (fileCount || 0) > 0 && !isSingleFile && (
                        <div className="flex items-center justify-between">
                            <small className="select-none">Files:</small>
                            <small className="mono text-gray-300">{fileCount}</small>
                        </div>
                    )}

                    {/* Upload Size */}
                    {!!uploadSize && (
                        <div className="flex items-center justify-between">
                            <small className="select-none">Upload Size:</small>
                            <small className="mono text-gray-300">{formatBytes(uploadSize)}</small>
                        </div>
                    )}

                    {/* Usage */}
                    {usage && usage.usageAmount > 0 && !isSingleFile && (
                        <div className="flex items-center justify-between">
                            <small className="select-none">Usage:</small>
                            <small className="mono text-gray-300">
                                {formatBytes(usage?.usageAmount || 0)}
                            </small>
                        </div>
                    )}

                    {processingMode && (
                        <div className="flex items-center justify-between">
                            <small className="select-none whitespace-nowrap">
                                Processing Mode:
                            </small>
                            <small className="mono text-gray-300 overflow-hidden whitespace-nowrap text-ellipsis">
                                {processingMode}
                            </small>
                        </div>
                    )}

                    {/* Date Uploaded */}
                    {dateCreated && (
                        <div className="flex items-center justify-between">
                            <small className="select-none whitespace-nowrap">Date Created:</small>
                            <small className="mono text-gray-300 overflow-hidden whitespace-nowrap text-ellipsis">
                                {dateCreated}
                            </small>
                        </div>
                    )}

                    {/* Upload Date Range */}
                    {startDate && endDate && !isSingleFile && (
                        <div className="flex justify-between">
                            <small className="select-none">Date & Time:</small>
                            <span>
                                <small className="mono text-gray-300 whitespace-nowrap overflow-hidden text-ellipsis">
                                    {startDate}
                                </small>
                                <br />
                                <small className="mono text-gray-300 whitespace-nowrap overflow-hidden text-ellipsis">
                                    {endDate}
                                </small>
                            </span>
                        </div>
                    )}
                    {isSingleFile && startDate && (
                        <div className="flex justify-between">
                            <small className="select-none">Date & Time:</small>
                            <small className="mono text-gray-300">{startDate}</small>
                        </div>
                    )}
                </div>
            </div>
        )
    }
)
InspectorPanelMetadata.displayName = "InspectorPanelMetadata"
export { InspectorPanelMetadata }
